import { useMemo, useState, type FormEvent } from "react";
import {
  TenantApiError,
  type TenantMenuImportPreviewItem,
  type TenantMenuImportPreviewResponse,
} from "@qr-menu/api-tenant";
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@qr-menu/ui";
import { tenantApi } from "../lib/api";

type SourceMode = "csv" | "url" | "pdf";

type PreviewState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; response: TenantMenuImportPreviewResponse };

type ConfirmState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "error"; message: string }
  | { kind: "done"; importedRows: number; skippedRows: number };

function describe(err: unknown, fallback: string): string {
  // TenantApiError.message carries the backend's curated public
  // error_message, never a raw cause or response body.
  if (err instanceof TenantApiError) {
    return err.message;
  }
  return fallback;
}

// Parsed only from the tenant's own current URL input, client-side, purely
// for display — the backend never returns a source host or URL, and this
// panel never asks it to.
function parsedHostname(rawUrl: string): string | null {
  try {
    const parsed = new URL(rawUrl);
    return parsed.hostname || null;
  } catch {
    return null;
  }
}

export function MenuImportRoute() {
  const [mode, setMode] = useState<SourceMode>("csv");

  // CSV form state.
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [delimiter, setDelimiter] = useState<"," | ";">(",");
  const [decimalSeparator, setDecimalSeparator] = useState<"." | ",">(".");

  // URL form state.
  const [url, setUrl] = useState("");

  // PDF form state.
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  const [preview, setPreview] = useState<PreviewState>({ kind: "idle" });
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [confirmState, setConfirmState] = useState<ConfirmState>({
    kind: "idle",
  });

  const busy =
    preview.kind === "loading" || confirmState.kind === "submitting";

  function resetPreview() {
    setPreview({ kind: "idle" });
    setSelectedIds(new Set());
    setConfirmState({ kind: "idle" });
  }

  function handleModeChange(next: SourceMode) {
    if (mode === next) return;
    setMode(next);
    resetPreview();
  }

  async function handleCsvSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;

    if (!csvFile) {
      setPreview({ kind: "error", message: "Bir CSV dosyası seçin." });
      return;
    }

    setPreview({ kind: "loading" });
    setConfirmState({ kind: "idle" });
    try {
      const response = await tenantApi.previewMenuImportCSV({
        file: csvFile,
        delimiter,
        decimal_separator: decimalSeparator,
      });
      setPreview({ kind: "ready", response });
      setSelectedIds(defaultSelection(response.items));
    } catch (err) {
      if (!(err instanceof TenantApiError && err.status === 401)) {
        setPreview({
          kind: "error",
          message: describe(err, "CSV önizlemesi oluşturulamadı."),
        });
      }
    }
  }

  async function handleUrlSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;

    const trimmedUrl = url.trim();
    if (trimmedUrl === "") {
      setPreview({ kind: "error", message: "Bir web adresi girin." });
      return;
    }

    setPreview({ kind: "loading" });
    setConfirmState({ kind: "idle" });
    try {
      const response = await tenantApi.previewMenuImportURL({
        url: trimmedUrl,
      });
      setPreview({ kind: "ready", response });
      setSelectedIds(defaultSelection(response.items));
    } catch (err) {
      if (!(err instanceof TenantApiError && err.status === 401)) {
        setPreview({
          kind: "error",
          message: describe(
            err,
            "Sayfa önizlemesi oluşturulamadı.",
          ),
        });
      }
    }
  }

  async function handlePdfSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;

    if (!pdfFile) {
      setPreview({ kind: "error", message: "Bir PDF dosyası seçin." });
      return;
    }

    setPreview({ kind: "loading" });
    setConfirmState({ kind: "idle" });
    try {
      const response = await tenantApi.previewMenuImportPDF({
        file: pdfFile,
      });
      setPreview({ kind: "ready", response });
      setSelectedIds(defaultSelection(response.items));
    } catch (err) {
      if (!(err instanceof TenantApiError && err.status === 401)) {
        setPreview({
          kind: "error",
          message: describe(err, "PDF önizlemesi oluşturulamadı."),
        });
      }
    }
  }

  function toggleSelected(id: number) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  async function handleConfirm() {
    if (preview.kind !== "ready" || busy) return;
    if (selectedIds.size === 0) {
      setConfirmState({
        kind: "error",
        message: "Onaylamak için en az bir satır seçin.",
      });
      return;
    }

    setConfirmState({ kind: "submitting" });
    try {
      const res = await tenantApi.confirmMenuImport(preview.response.job_id, {
        item_ids: Array.from(selectedIds),
      });
      setConfirmState({
        kind: "done",
        importedRows: res.imported_rows,
        skippedRows: res.skipped_rows,
      });
    } catch (err) {
      if (!(err instanceof TenantApiError && err.status === 401)) {
        setConfirmState({
          kind: "error",
          message: describe(err, "İçe aktarma onaylanamadı."),
        });
      }
    }
  }

  const hostname = mode === "url" ? parsedHostname(url.trim()) : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>İçe Aktar</CardTitle>
        <CardDescription>
          Menünüzü CSV dosyasından veya bir web adresinden önizleyip
          onaylayarak içe aktarın. Hiçbir kategori veya ürün, siz onaylamadan
          oluşturulmaz.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="create-form">
          <p className="form-heading">Kaynak</p>
          <div className="create-form-grid">
            <div className="filter-actions">
              <button
                type="button"
                className={mode === "csv" ? "primary" : ""}
                onClick={() => handleModeChange("csv")}
                disabled={busy}
              >
                CSV
              </button>
              <button
                type="button"
                className={mode === "url" ? "primary" : ""}
                onClick={() => handleModeChange("url")}
                disabled={busy}
              >
                URL
              </button>
              <button
                type="button"
                className={mode === "pdf" ? "primary" : ""}
                onClick={() => handleModeChange("pdf")}
                disabled={busy}
              >
                PDF
              </button>
            </div>
          </div>
        </div>

        {mode === "csv" ? (
          <form className="create-form" onSubmit={handleCsvSubmit}>
            <p className="form-heading">CSV önizlemesi</p>
            <div className="create-form-grid">
              <Field label="Dosya" htmlFor="import-csv-file">
                <input
                  id="import-csv-file"
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => setCsvFile(e.target.files?.[0] ?? null)}
                  disabled={busy}
                />
              </Field>
              <Field label="Ayraç" htmlFor="import-delimiter">
                <select
                  id="import-delimiter"
                  value={delimiter}
                  onChange={(e) => setDelimiter(e.target.value as "," | ";")}
                  disabled={busy}
                >
                  <option value=",">Virgül (,)</option>
                  <option value=";">Noktalı virgül (;)</option>
                </select>
              </Field>
              <Field label="Ondalık ayracı" htmlFor="import-decimal-separator">
                <select
                  id="import-decimal-separator"
                  value={decimalSeparator}
                  onChange={(e) =>
                    setDecimalSeparator(e.target.value as "." | ",")
                  }
                  disabled={busy}
                >
                  <option value=".">Nokta (.)</option>
                  <option value=",">Virgül (,)</option>
                </select>
              </Field>
              <div className="filter-actions">
                <button type="submit" className="primary" disabled={busy}>
                  {preview.kind === "loading"
                    ? "Önizleme oluşturuluyor…"
                    : "Önizleme oluştur"}
                </button>
              </div>
            </div>
          </form>
        ) : mode === "url" ? (
          <form className="create-form" onSubmit={handleUrlSubmit}>
            <p className="form-heading">URL önizlemesi</p>
            <div className="create-form-grid">
              <Field label="Web adresi" htmlFor="import-url">
                <input
                  id="import-url"
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://ornek-restoran.com/menu"
                  disabled={busy}
                />
              </Field>
              <div className="filter-actions">
                <button type="submit" className="primary" disabled={busy}>
                  {preview.kind === "loading"
                    ? "Önizleme oluşturuluyor…"
                    : "Önizleme oluştur"}
                </button>
              </div>
            </div>
            {hostname && (
              <p className="hint" style={{ marginTop: 8, marginBottom: 0 }}>
                Hedef: {hostname}
              </p>
            )}
          </form>
        ) : (
          <form className="create-form" onSubmit={handlePdfSubmit}>
            <p className="form-heading">PDF önizlemesi</p>
            <div className="create-form-grid">
              <Field label="Dosya" htmlFor="import-pdf-file">
                <input
                  id="import-pdf-file"
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
                  disabled={busy}
                />
              </Field>
              <div className="filter-actions">
                <button type="submit" className="primary" disabled={busy}>
                  {preview.kind === "loading"
                    ? "Önizleme oluşturuluyor…"
                    : "Önizleme oluştur"}
                </button>
              </div>
            </div>
            <p className="hint" style={{ marginTop: 8, marginBottom: 0 }}>
              Yalnızca metin tabanlı PDF'ler desteklenir; taranmış (görsel) PDF'ler
              içe aktarılamaz.
            </p>
          </form>
        )}

        {preview.kind === "error" && (
          <div className="state error">{preview.message}</div>
        )}
        {preview.kind === "loading" && (
          <div className="state">Önizleme oluşturuluyor…</div>
        )}

        {preview.kind === "ready" && (
          <MenuImportPreviewBody
            response={preview.response}
            selectedIds={selectedIds}
            onToggle={toggleSelected}
            busy={busy}
            confirmState={confirmState}
            onConfirm={handleConfirm}
          />
        )}
      </CardContent>
    </Card>
  );
}

function defaultSelection(items: TenantMenuImportPreviewItem[]): Set<number> {
  const ids = new Set<number>();
  for (const item of items) {
    if (item.status === "valid") {
      ids.add(item.id);
    }
  }
  return ids;
}

interface MenuImportPreviewBodyProps {
  response: TenantMenuImportPreviewResponse;
  selectedIds: Set<number>;
  onToggle: (id: number) => void;
  busy: boolean;
  confirmState: ConfirmState;
  onConfirm: () => void;
}

function MenuImportPreviewBody({
  response,
  selectedIds,
  onToggle,
  busy,
  confirmState,
  onConfirm,
}: MenuImportPreviewBodyProps) {
  const validCount = useMemo(
    () => response.items.filter((item) => item.status === "valid").length,
    [response.items],
  );

  if (confirmState.kind === "done") {
    return (
      <div className="state ok">
        İçe aktarma onaylandı: {confirmState.importedRows} ürün eklendi,{" "}
        {confirmState.skippedRows} satır atlandı.
      </div>
    );
  }

  return (
    <>
      <p className="hint" style={{ marginTop: 16 }}>
        Toplam {response.total_rows} satır — {response.valid_rows} geçerli,{" "}
        {response.invalid_rows} geçersiz. Onaylamak istediğiniz satırları
        seçin.
      </p>

      {validCount === 0 ? (
        <div className="state">Onaylanabilir geçerli satır bulunamadı.</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableCell as="th" style={{ width: 40 }} />
              <TableCell as="th">Kategori</TableCell>
              <TableCell as="th">Ürün</TableCell>
              <TableCell as="th">Fiyat</TableCell>
              <TableCell as="th">Durum</TableCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {response.items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(item.id)}
                    disabled={busy || item.status !== "valid"}
                    onChange={() => onToggle(item.id)}
                  />
                </TableCell>
                <TableCell className="wrap-cell">
                  {item.category_name || <span className="hint">—</span>}
                  {item.will_create_category && (
                    <>
                      {" "}
                      <Badge variant="success">yeni</Badge>
                    </>
                  )}
                </TableCell>
                <TableCell className="wrap-cell">
                  {item.name || <span className="hint">—</span>}
                  {item.duplicate && (
                    <>
                      {" "}
                      <Badge variant="danger">tekrar</Badge>
                    </>
                  )}
                </TableCell>
                <TableCell>
                  {item.price ? `${item.price} ${item.currency}` : "—"}
                </TableCell>
                <TableCell>
                  {item.status === "valid" ? (
                    <Badge variant="success">Geçerli</Badge>
                  ) : (
                    <Badge variant="danger">Geçersiz</Badge>
                  )}
                  {item.errors.length > 0 && (
                    <div className="hint">
                      {item.errors.map((e) => e.field).join(", ")}
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {confirmState.kind === "error" && (
        <div className="state error">{confirmState.message}</div>
      )}

      <div className="filter-actions" style={{ marginTop: 12 }}>
        <button
          type="button"
          className="primary"
          onClick={onConfirm}
          disabled={busy || selectedIds.size === 0}
        >
          {confirmState.kind === "submitting"
            ? "Onaylanıyor…"
            : `Seçilenleri onayla (${selectedIds.size})`}
        </button>
      </div>
    </>
  );
}
